import { morph } from '@theme/morph';
import { VariantUpdateEvent } from '@theme/events';

/**
 * Cautiva PDP variant picker — progressive enhancement over the anchor-based
 * <a href="?variant=X"> picker rendered by snippets/cautiva-variant-picker.liquid.
 *
 * On selection it fetches the re-rendered product page for the chosen variant
 * and morphs the server-authored regions (picker, price, discount badge, sticky
 * ATC), then dispatches Horizon's VariantUpdateEvent so the stock
 * product-form-component updates the add-to-cart button. No pricing or
 * availability logic is reproduced client-side — the server stays authoritative,
 * and full-page navigation remains the no-JS fallback.
 */
class CautivaVariantPicker extends HTMLElement {
  #abortController;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#abortController?.abort();
  }

  #onClick = (event) => {
    const link = event.target.closest('a.cautiva-variant-picker__value');
    if (!link || !this.contains(link)) return;

    const variantId = new URL(link.href, window.location.origin).searchParams.get('variant');
    if (!variantId) return;

    event.preventDefault();

    // Optimistic selection within the clicked option group for snappy feedback;
    // the morph below reconciles it against the authoritative server render.
    const group = link.closest('.cautiva-variant-picker__option');
    group
      ?.querySelectorAll('.cautiva-variant-picker__value.is-selected')
      .forEach((selected) => selected.classList.remove('is-selected'));
    link.classList.add('is-selected');

    this.#fetchVariant(variantId);
  };

  #fetchVariant(variantId) {
    const productUrl = (this.dataset.productUrl || window.location.pathname).split('?')[0];
    const requestUrl = `${productUrl}?variant=${encodeURIComponent(variantId)}`;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const doc = new DOMParser().parseFromString(responseText, 'text/html');
        this.#applyUpdate(doc, variantId);
        this.#updateUrl(variantId);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        // Network failure — hand off to a full navigation so the shopper still
        // reaches the correct variant state.
        window.location.assign(requestUrl);
      });
  }

  #applyUpdate(doc, variantId) {
    const newPicker = doc.querySelector('cautiva-variant-picker');
    if (newPicker) morph(this, newPicker);

    // Cautiva-custom regions have no VariantUpdate listener of their own.
    this.#morphRegion(doc, '.cautiva-pdp-main__price-block');
    this.#morphRegion(doc, '.cautiva-pdp-main__gallery-item');
    this.#morphRegion(doc, '.cautiva-sticky-atc__inner');

    // Hand the add-to-cart buttons to Horizon's product-form-component, which
    // already listens for this bubbling event on the closest section.
    const idInput = doc.querySelector('.cautiva-pdp-main__form input[name="id"]');
    const available = !!idInput && !idInput.hasAttribute('disabled');
    const productId = this.dataset.productId ?? '';

    this.dispatchEvent(
      new VariantUpdateEvent({ id: variantId, available }, productId, { html: doc, productId })
    );

    this.#announce(available);
  }

  #announce(available) {
    const status = document.querySelector(`[data-cautiva-variant-status="${this.dataset.productId ?? ''}"]`);
    if (!status) return;

    const selection = Array.from(this.querySelectorAll('.cautiva-variant-picker__legend-value'))
      .map((legend) => legend.textContent.trim())
      .filter(Boolean)
      .join(', ');

    status.textContent = available ? selection : `${selection}${selection ? ' — ' : ''}Sin stock`;
  }

  #morphRegion(doc, selector) {
    const current = document.querySelector(selector);
    const next = doc.querySelector(selector);
    if (current && next) morph(current, next);
  }

  #updateUrl(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    history.replaceState({}, '', url.toString());
  }
}

if (!customElements.get('cautiva-variant-picker')) {
  customElements.define('cautiva-variant-picker', CautivaVariantPicker);
}
